import React, { useState } from 'react';
import { PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import type { CaseEntry, CaseEntryType, CaseEntryUnit } from '../../../types/caseEntry';
import type { QuoteLine } from '../../../types/quote';
import { entrySubtotal, sortEntries } from '../../../utils/caseEntries';
import { diffTone, formatQuantity, guessEntryType, signedKr } from '../../../utils/caseEstimate';
import { formatKr, lineSubtotal } from '../../../utils/quote';
import { parseAmount, toInput, todayIso } from './useCaseEntryForm';
interface CostSectionProps {
  title: string;
  addLabel: string;
  /** Sagstyper der hører til sektionen. */
  types: CaseEntryType[];
  /** Vis kategori (fx "Maskiner") på hver linje — bruges i Øvrigt. */
  showCategory?: boolean;
  emptyText: string;
  estimateLines: QuoteLine[];
  /** Alle linjer på sagen — bruges når faktisk forbrug på en tilbudslinje gemmes. */
  allEntries: CaseEntry[];
  editingId: string | null;
  saving: boolean;
  onPersist: (next: CaseEntry[]) => Promise<boolean>;
  onEdit: (entry: CaseEntry) => void;
  onDelete: (entry: CaseEntry) => void;
  onAdd: () => void;
}
const iconButton = 'rounded-lg p-2 text-ink-faint transition-colors duration-150 hover:bg-canvas-deep';

/** Linjer med "fast pris" sammenlignes på beløb; øvrige på antal. */
const isFixed = (unit: string) => unit === 'fast pris';

/**
 * Fælles opbygning for Materialer og Øvrigt: tilbuddets linjer som estimat,
 * faktisk forbrug ved siden af, ekstra linjer tilføjet undervejs, og totaler.
 * Tilbuddet ændres aldrig — det faktiske forbrug gemmes som sagslinjer.
 */
export function CostSection({
  title,
  addLabel,
  types,
  showCategory = false,
  emptyText,
  estimateLines,
  allEntries,
  editingId,
  saving,
  onPersist,
  onEdit,
  onDelete,
  onAdd
}: CostSectionProps) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [rowError, setRowError] = useState<{
    id: string;
    text: string;
  } | null>(null);
  const own = allEntries.filter(entry => types.includes(entry.type));
  const quotedIds = new Set(estimateLines.map(line => line.id));
  const linkedFor = (lineId: string) => allEntries.find(entry => entry.quoteLineId === lineId);
  const extras = sortEntries(own.filter(entry => !entry.quoteLineId || !quotedIds.has(entry.quoteLineId)));
  const estimated = estimateLines.reduce((sum, line) => sum + lineSubtotal(line), 0);
  const registered = own.reduce((sum, entry) => sum + entrySubtotal(entry), 0);
  const hasEstimate = estimateLines.length > 0;

  /** Faktisk værdi for en tilbudslinje: antal, eller beløb ved fast pris. */
  const actualValue = (line: QuoteLine, entry?: CaseEntry) => entry ? isFixed(line.unit) ? entrySubtotal(entry) : entry.quantity : undefined;
  async function setActual(line: QuoteLine, value: number) {
    const linked = linkedFor(line.id);
    if (value === (actualValue(line, linked) ?? 0) && linked) return;
    const now = new Date().toISOString();
    const fixed = isFixed(line.unit);
    const quantity = fixed ? 1 : value;
    const unitPrice = fixed ? value : line.unitPrice;
    let next: CaseEntry[];
    if (value === 0) {
      if (!linked) return;
      next = allEntries.filter(entry => entry.quoteLineId !== line.id);
    } else if (linked) {
      next = allEntries.map(entry => entry.id === linked.id ? {
        ...entry,
        quantity,
        unitPrice,
        updatedAt: now
      } : entry);
    } else {
      next = [...allEntries, {
        id: `entry-${Math.random().toString(36).slice(2, 10)}`,
        type: guessEntryType(line),
        date: todayIso(),
        description: line.description || title,
        quantity,
        unit: line.unit as CaseEntryUnit,
        unitPrice,
        vatRate: line.vatRate,
        quoteLineId: line.id,
        createdAt: now,
        updatedAt: now
      }];
    }
    await onPersist(next);
  }
  async function commit(line: QuoteLine) {
    const raw = drafts[line.id];
    if (raw === undefined) return;
    const value = raw.trim() === '' ? 0 : parseAmount(raw);
    if (!Number.isFinite(value) || value < 0) {
      setRowError({
        id: line.id,
        text: isFixed(line.unit) ? 'Skriv et gyldigt beløb, fx 450.' : 'Skriv et gyldigt antal, fx 3 eller 2,5.'
      });
      return;
    }
    setRowError(null);
    setDrafts(({
      [line.id]: _removed,
      ...rest
    }) => rest);
    await setActual(line, value);
  }
  const subLabel = (source: string, type: CaseEntryType) => showCategory ? `${source} · ${type}` : source;
  return <div data-id="element-129" data-mp="3od8t2z2z9i27">
      <div className="flex items-center justify-between gap-3" data-id="element-130" data-mp="3ocz3vdprbexk">
        <h3 className="font-display text-base font-bold tracking-tight text-ink" data-id="element-131" data-mp="3anf5ek37l3u2">{title}</h3>
        <button type="button" onClick={onAdd} className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-accent transition-colors duration-150 hover:text-accent-deep" data-id="element-132" data-mp="3eo8on05phugm">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-133" data-mp="b072l6xnj8uz" />
          {addLabel}
        </button>
      </div>

      {estimateLines.length === 0 && extras.length === 0 ? <p className="mt-2 text-sm text-ink-muted" data-id="element-134" data-mp="1qr99lvqfmzft">{emptyText}</p> : <div className="mt-3 overflow-x-auto" data-id="element-135" data-mp="25fm7wzczsudh">
          <table className="w-full min-w-[36rem] text-[15px]" data-id="element-136" data-mp="25eaxwhsyeqdx">
            <thead data-id="element-137" data-mp="20fzm41j3m5t2">
              <tr className="border-b border-line text-left text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-138" data-mp="20hkhtuh5fusi">
                <th scope="col" className="pb-2 pr-3 font-semibold" data-id="element-139" data-mp="1cr6xyr8px290">Beskrivelse</th>
                <th scope="col" className="pb-2 pr-3 text-right font-semibold" data-id="element-140" data-mp="17t5dq2zk2wvy">Estimeret</th>
                <th scope="col" className="pb-2 pr-3 text-right font-semibold" data-id="element-141" data-mp="1mq5zlpv2fxmi">Faktisk</th>
                <th scope="col" className="pb-2 pr-3 text-right font-semibold" data-id="element-142" data-mp="1hp02zgyabq7p">Enhedspris</th>
                <th scope="col" className="pb-2 text-right font-semibold" data-id="element-143" data-mp="2y3i482yngk9q">Total</th>
                <th scope="col" className="w-20 pb-2" data-id="element-144" data-mp="2t5q26g4ajhrt"><span className="sr-only" data-id="element-145" data-mp="2t4f14o28co4e">Handling</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/70" data-id="element-146" data-mp="307ljxdxax6da">
              {estimateLines.map(line => {
            const linked = linkedFor(line.id);
            const fixed = isFixed(line.unit);
            const value = drafts[line.id] ?? toInput(actualValue(line, linked));
            const error = rowError?.id === line.id ? rowError.text : '';
            return <tr key={line.id} className="align-middle" data-id="element-147" data-mp="2hj858k430q5x">
                    <td className="py-2.5 pr-3" data-id="element-148" data-mp="2mgqfcdtu5bfi">
                      <p className="text-ink" data-id="element-149" data-mp="2mhhz8re2lkpy">{line.description || title}</p>
                      <p className="text-xs text-ink-muted" data-id="element-150" data-mp="2rf1qcrvv9rzb">{subLabel('Fra tilbud', guessEntryType(line))}</p>
                      {error && <p role="alert" className="text-xs text-danger" data-id="element-151" data-mp="2w8cmnz31yqcz">{error}</p>}
                    </td>
                    <td className="whitespace-nowrap py-2.5 pr-3 text-right text-ink-soft" data-id="element-152" data-mp="1yr5jwvghwlpc">
                      {fixed ? formatKr(lineSubtotal(line)) : `${formatQuantity(line.quantity || 0)} ${line.unit}`}
                    </td>
                    <td className="py-2.5 pr-3 text-right" data-id="element-153" data-mp="gtsql60tjj9d">
                      <label className="inline-flex items-center gap-1.5" data-id="element-154" data-mp="guak3uw3jjdl">
                        <span className="sr-only" data-id="element-155" data-mp="btmwzkaznmon">
                          {fixed ? 'Faktisk beløb' : 'Faktisk antal'} for {line.description}
                        </span>
                        <input inputMode="decimal" placeholder="0" value={value} disabled={saving} onChange={event => setDrafts(current => ({
                    ...current,
                    [line.id]: event.target.value
                  }))} onBlur={() => commit(line)} onKeyDown={event => {
                    if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
                  }} aria-invalid={error ? true : undefined} className={`${fixed ? 'w-20' : 'w-16'} rounded-lg border bg-white px-2 py-1.5 text-right text-[15px] text-ink transition-colors duration-150 focus:border-accent focus:outline-none ${error ? 'border-danger' : 'border-line hover:border-ink/25'}`} data-id="element-156" data-mp="1x1pmsbhxzhn" />
                        <span className="whitespace-nowrap text-sm text-ink-muted" data-id="element-157" data-mp="y3v0mbmvcr7f">{fixed ? 'kr.' : line.unit}</span>
                      </label>
                    </td>
                    <td className="whitespace-nowrap py-2.5 pr-3 text-right text-ink-soft" data-id="element-158" data-mp="o5f7620dd5ur">
                      {fixed ? <span className="text-ink-faint" data-id="element-159" data-mp="ae0qi1txpgqv">fast pris</span> : formatKr(line.unitPrice)}
                    </td>
                    <td className="whitespace-nowrap py-2.5 text-right font-medium text-ink" data-id="element-160" data-mp="f969osvychb9">
                      {linked ? formatKr(entrySubtotal(linked)) : <span className="text-ink-faint" data-id="element-161" data-mp="5dmfqc0y7sp5">—</span>}
                    </td>
                    <td className="py-2.5 pl-2 text-right" data-id="element-162" data-mp="3hwfe1fbyi8wp">
                      {!linked && <button type="button" disabled={saving} onClick={() => setActual(line, fixed ? lineSubtotal(line) : line.quantity || 0)} className="whitespace-nowrap text-xs font-medium text-accent transition-colors duration-150 hover:text-accent-deep disabled:opacity-50" data-id="element-163" data-mp="3dvvhodz7j9bm">
                          Brug estimat
                        </button>}
                    </td>
                  </tr>;
          })}
              {extras.map(entry => <tr key={entry.id} className={`align-middle ${editingId === entry.id ? 'bg-accent/5' : ''}`} data-id="element-164" data-mp="9i6susbrkkek">
                  <td className="py-2.5 pr-3" data-id="element-165" data-mp="of7imqaea5ae">
                    <p className="text-ink" data-id="element-166" data-mp="jfbjp5gk141p">{entry.description || entry.type}</p>
                    <p className="text-xs text-ink-muted" data-id="element-167" data-mp="jd8kbutihykb">{subLabel('Tilføjet undervejs', entry.type)}</p>
                  </td>
                  <td className="py-2.5 pr-3 text-right text-ink-faint" data-id="element-168" data-mp="27hb8tktzbc8f">—</td>
                  <td className="whitespace-nowrap py-2.5 pr-3 text-right text-ink" data-id="element-169" data-mp="27f8amdioacsm">
                    {isFixed(entry.unit) ? formatKr(entrySubtotal(entry)) : `${formatQuantity(entry.quantity)} ${entry.unit}`}
                  </td>
                  <td className="whitespace-nowrap py-2.5 pr-3 text-right text-ink-soft" data-id="element-170" data-mp="2v6vvoet96tmz">
                    {isFixed(entry.unit) ? <span className="text-ink-faint" data-id="element-171" data-mp="2q8lwzu8blawm">fast pris</span> : formatKr(entry.unitPrice)}
                  </td>
                  <td className="whitespace-nowrap py-2.5 text-right font-medium text-ink" data-id="element-172" data-mp="2m4ebe5x5sb2m">{formatKr(entrySubtotal(entry))}</td>
                  <td className="py-2.5 pl-2 text-right" data-id="element-173" data-mp="2h97dxfuhg2m5">
                    <div className="inline-flex gap-0.5" data-id="element-174" data-mp="1ark8v62x4wv8">
                      <button type="button" onClick={() => onEdit(entry)} aria-label={`Ret ${entry.description}`} className={`${iconButton} hover:text-ink`} data-id="element-175" data-mp="15rz9iuju6mhz">
                        <PencilIcon className="h-4 w-4" aria-hidden="true" data-id="element-176" data-mp="3hgvuj9fvpfbz" />
                      </button>
                      <button type="button" onClick={() => onDelete(entry)} aria-label={`Slet ${entry.description}`} className={`${iconButton} hover:text-danger`} data-id="element-177" data-mp="3mc1jyi8hpc4t">
                        <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-178" data-mp="3v795zlrzbc62" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>}

      <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm" data-id="element-179" data-mp="dycer6ee01y2">
        {hasEstimate && <div className="flex gap-1.5" data-id="element-180" data-mp="dwhu7t712bui">
            <dt className="text-ink-muted" data-id="element-181" data-mp="hywb47r4z1j5">Samlet estimeret</dt>
            <dd className="font-medium text-ink" data-id="element-182" data-mp="2fpp7kx32yjdr">{formatKr(estimated)}</dd>
          </div>}
        <div className="flex gap-1.5" data-id="element-183" data-mp="2jrarh2e17xxg">
          <dt className="text-ink-muted" data-id="element-184" data-mp="2jsc1d3jbaedl">Samlet registreret</dt>
          <dd className="font-medium text-ink" data-id="element-185" data-mp="3n1yblan2dmlx">{formatKr(registered)}</dd>
        </div>
        {hasEstimate && <div className="flex gap-1.5" data-id="element-186" data-mp="3i3mzet9ar00j">
            <dt className="text-ink-muted" data-id="element-187" data-mp="3i4y4qxie4ftr">Forskel</dt>
            <dd className={`font-medium ${diffTone(registered - estimated)}`} data-id="element-188" data-mp="3w3ecmrrzocdh">{signedKr(registered - estimated)}</dd>
          </div>}
      </dl>
    </div>;
}